import { FieldType, LogicGroup, LogicModifier, LogicType, Validation } from '../../types/recap'
import RecapField from '../components/RecapField.vue'
import { Ref, ref } from 'vue'
import { errorMessages } from '../../utils/error-messages'
import { parseBool } from '../../utils'
import { addBreadcrumb } from '@sentry/vue'

export function useLogic(selfReference: any, dependenceRefs: any[]) {
  const selfRef = ref<any>(selfReference)
  const dependencyRefs = ref<(typeof RecapField)[]>(dependenceRefs as (typeof RecapField)[])

  function checkValidation(repeatingIndex: number | undefined): string[] {
    const tempDependenceRefs = filterDependencyRefsForRepeatingForm(dependencyRefs, repeatingIndex)

    const errorMessages = selfRef.value
      .getValidations()
      .map((validation: Validation) => {
        if (validation.references_events_custom_instances_id) {
          // For each of the validations, find the ref it depends on if there is one
          const referencedRefs = tempDependenceRefs.filter((depRef) => {
            return (depRef as any).getFieldId() === validation.references_events_custom_instances_id
          })
          if (!referencedRefs.length) {
            return 'Invalid field reference'
          }

          return runValidation(selfRef.value as typeof RecapField, validation, referencedRefs[0])
        }
        // Validation does not depend on another field, just run it
        return runValidation(selfRef.value as typeof RecapField, validation)
      })
      .filter((message: string) => message) // Filter out any empty messages
    if (errorMessages.length) {
      addBreadcrumb({
        category: 'RecapValidationErrors',
        level: 'debug',
        message: 'Recap submission failed validation',
        data: {
          errorMessages,
        },
      })
    }
    return errorMessages
  }

  function checkLogicConditions(repeatingIndex: number | undefined) {
    const tempDependenceRefs = filterDependencyRefsForRepeatingForm(dependencyRefs, repeatingIndex)

    // Ensure that every dependent field meets its corresponding logic conditions
    const logicGroups = selfRef.value.getLogicGroups() as LogicGroup[]
    const results: boolean[] = []
    for (const group of logicGroups) {
      const conditionResults: boolean[] = []
      const conditions = group.conditions
      if (conditions.length) {
        for (const condition of conditions) {
          const referencedField = tempDependenceRefs.find(
            (ref) => (ref as any).getFieldId() === condition.references_events_custom_instances_id
          )
          if (referencedField) {
            const logic = condition.logic
            const val = referencedField.getValue() as boolean | any[] | null
            const conditionValue = condition.selected_option_identifier
              ? condition.selected_option_identifier
              : condition.value
            const result = compareValues(
              referencedField.getFieldType() as FieldType,
              logic,
              conditionValue,
              val
            )
            conditionResults.push(result)
            if (!result) {
              // Stop comparing, we have a falsy logic condition
              break
            }
          } else {
            // Could not find the referenced field, mark this as false
            conditionResults.push(false)
          }
        }
        // Mark the group result true or false depending on if every conditionw was true or false
        results.push(conditionResults.every((result) => !!result))
      }
    }
    if (results.length) {
      // If any of the logic groups was true, the field should be visible
      return results.some((result) => !!result)
    }
    // If there were no logic groups, or no logic groups had any condition results,
    // mark the condition result as true by default
    return true
  }

  function runLogicModifiers(repeatingIndex: number | undefined) {
    // Don't bother running logic modifiers if the field is hidden
    if (!selfRef.value.getIsVisible()) {
      return
    }

    // Gather all relevant modifiers
    const logicGroups = selfRef.value.getLogicGroups() as LogicGroup[]
    let modifiers: (LogicModifier | undefined)[] = []
    if (logicGroups && logicGroups.length) {
      logicGroups.forEach((group) => {
        modifiers = modifiers.concat(group.modifiers)
      })
    }

    if (modifiers && modifiers.length) {
      // Currently there should only ever be 0 or 1 modifiers
      // If we make it here, we have 1
      const modifier = modifiers[0]
      if (!modifier) return

      const tempDependenceRefs = filterDependencyRefsForRepeatingForm(
        dependencyRefs,
        repeatingIndex
      )

      const isAnyDependent = !!modifier.references_events_custom_instances_id

      let val: boolean | any[] | null = null
      let dependentRef: typeof RecapField | null = null
      if (isAnyDependent) {
        dependentRef = tempDependenceRefs.find((ref) => {
          return (
            modifier.references_events_custom_instances_id === (ref as unknown as any).getFieldId()
          )
        }) as typeof RecapField
        val = dependentRef ? ((dependentRef as any).getValue() as boolean | any[] | null) : null
      } else {
        val = selfRef.value.getValue() as boolean | any[] | null
      }

      // Ensure that every dependent field meets its corresponding logic condition
      const logic = modifier.logic
      const modifierValue = modifier.selected_events_custom_options_id
        ? modifier.selected_events_custom_options_id
        : modifier.value
      const fieldType = dependentRef
        ? ((dependentRef as any).getFieldType() as FieldType)
        : (selfRef.value.getFieldType() as FieldType)
      const result = compareValues(fieldType, logic, modifierValue, val)

      // Run the modifier based on the result
      // Get the different options
      const truthyOptions = modifier.set_options
        .filter((option) => parseBool(option.truthy_value))
        .map((option) => option.id)
      const falsyOptions = modifier.set_options
        .filter((option) => !parseBool(option.truthy_value))
        .map((option) => option.id)
      const { options, filteredOptionsRef, resetValue } = selfRef.value.getOptions()
      const currentOptions = filteredOptionsRef.value.map((option: any) => option.value)
      let newOptions: any[] = []
      if (!result) {
        newOptions = options.filter((option: any) => falsyOptions.includes(option.value as string))
      } else {
        newOptions = options.filter((option: any) => truthyOptions.includes(option.value as string))
      }
      if (
        !isArrayEqual(
          currentOptions as any[],
          newOptions.map((option: any) => option.value)
        )
      ) {
        filteredOptionsRef.value = newOptions
        resetValue()
      }
    }
  }

  /**
   * Checks to see if a field's value is valid by comparing the value against expected values for its field type.
   *
   * Currently this method only checks numeric fields and returns true for everything else, but this may need to be expanded in the future.
   */
  function isValidValue(value: any, secondValue: any, fieldType: FieldType): boolean {
    if (value !== null) {
      if (fieldType === 'FIELD_INTEGER' || fieldType === 'FIELD_TOTAL') {
        const parsedValue = Number(value)
        const invalidRegex = /^[0]+(?!\.)(?!$)/ // Some gross regex that makes sure there are no leading zeroes, unless there's a decimal, or it's the only zero
        return Number.isInteger(parsedValue) && !invalidRegex.test(String(value))
      } else if (
        fieldType === 'FIELD_FLOAT1' ||
        fieldType === 'FIELD_FLOAT2' ||
        fieldType === 'FIELD_MONEY'
      ) {
        return numberExists(parseFloat(String(value)))
      } else if (fieldType === 'FIELD_DURATION') {
        const parsedValue = Number(value)
        const parsedSecondValue = Number(secondValue)
        return (
          Number.isInteger(parsedValue) &&
          (secondValue === null || Number.isInteger(parsedSecondValue)) &&
          fieldType === 'FIELD_DURATION'
        )
      }
    }
    if (secondValue !== null) {
      const parsedSecondValue = Number(secondValue)
      return Number.isInteger(parsedSecondValue) && fieldType === 'FIELD_DURATION'
    }
    return true
  }

  return {
    checkLogicConditions,
    checkValidation,
    runLogicModifiers,
    isValidValue,
  }
}

function compareValues(
  fieldType: FieldType,
  logic: LogicType,
  comparisonValue: string | null,
  val: boolean | any[] | null
) {
  if (val === null || val === undefined || (val instanceof Array && val.length === 0)) {
    return false
  }

  let value: string | number | boolean | any[] | null = val
  if (fieldType === 'FIELD_YES_NO' || fieldType === 'FIELD_AGREEMENT_CHECKBOX') {
    value = val ? '1' : '0'
  }

  if (logic === 'PRESENT') {
    if (value === null || value === undefined) {
      return false
    }
  } else if (logic === 'EQUALS') {
    if (!value || comparisonValue !== value?.toString()) {
      return false
    }
  } else if (logic === 'DOES_NOT_EQUAL') {
    if (!value || comparisonValue === value?.toString()) {
      return false
    }
  } else if (logic === 'GREATER_THAN') {
    if (!value || !comparisonValue || comparisonValue >= value.toString()) {
      return false
    }
  } else if (logic === 'LESS_THAN') {
    if (!value || !comparisonValue || comparisonValue <= value.toString()) {
      return false
    }
  } else if (comparisonValue) {
    const comparisonTime = parseTime(comparisonValue, true)
    const valueTime = parseTime(value as unknown as string, false)

    if (logic === 'BEFORE_IN') {
      if (comparisonTime < valueTime) {
        return false
      }
    } else if (logic === 'AFTER_IN') {
      if (comparisonTime > valueTime) {
        return false
      }
    } else if (logic === 'BEFORE_EX') {
      if (comparisonTime <= valueTime) {
        return false
      }
    } else if (logic === 'AFTER_EX') {
      if (comparisonTime >= valueTime) {
        return false
      }
    }
  }
  return true
}

function runValidation(
  ref: typeof RecapField,
  validation: Validation,
  dependentRef?: typeof RecapField
) {
  const value = (ref as any).getValue()
  const depValue = dependentRef && (dependentRef as any).getValue()
  const validationValue = validation.value
  const type = validation.validation_type
  const fieldType = (ref as any).getFieldType() as FieldType
  const parseNumberMethod = fieldType === 'FIELD_INTEGER' ? parseInt : parseFloat
  const parsedNumberValue = parseNumberMethod(value as string)
  const parsedNumberValidationValue = parseNumberMethod(validation.value as string)
  const parsedNumberDepValue = parseNumberMethod(depValue as string)
  let message = ''

  const taskProperty = (ref as any).getTaskProperty()
  const isRaiReasonCodeAndDisplayed =
    fieldType === 'FIELD_RAI_REASON_CODE' ? (ref as any).getDisplayRaiReasonCodes() : true
  const isAdQuantityAndDisplayed =
    taskProperty === 'adsInstalled' ? (ref as any).getDisplayAdQuantity() : true
  const isQuantityReasonCodeAndDisplayed =
    taskProperty === 'quantityReasonCode' ? (ref as any).getDisplayQuantityReasonCode() : true
  const shouldEvaluateValidation =
    !(!(ref as any).getIsVisible() || (ref as any).shouldBeHidden) &&
    isRaiReasonCodeAndDisplayed &&
    isAdQuantityAndDisplayed &&
    isQuantityReasonCodeAndDisplayed

  if (!shouldEvaluateValidation) {
    return ''
  }

  if (type === 'responseRequired') {
    if (fieldType === 'FIELD_YES_NO' && value !== true && value !== false) {
      message = errorMessages.ERROR_FIELD_REQUIRED()
    } else if (
      fieldType !== 'FIELD_YES_NO' &&
      (value === null || value === undefined || (value instanceof Array && value.length === 0))
    ) {
      message = errorMessages.ERROR_FIELD_REQUIRED()
    }
  } else if (type === 'ltValue') {
    if (
      !numberExists(parsedNumberValue) ||
      !numberExists(parsedNumberValidationValue) ||
      parsedNumberValue >= parsedNumberValidationValue
    ) {
      message = errorMessages.ERROR_LESS_THAN_SPECIFIED_VALUE(validationValue as string)
    }
  } else if (type === 'lteValue') {
    // There's a special case where Neptune doesn't want this to display if it's the default integer validation.
    let changedValue = parsedNumberValue
    if (parsedNumberValidationValue === 922000000000 && !numberExists(parsedNumberValue)) {
      changedValue = 0
    } else if (
      !numberExists(changedValue) ||
      !numberExists(parsedNumberValidationValue) ||
      parsedNumberValue > parsedNumberValidationValue
    ) {
      message = errorMessages.ERROR_LESS_THAN_OR_EQUAL_TO_SPECIFIED_VALUE(validationValue as string)
    }
  } else if (type === 'gtValue') {
    if (
      !numberExists(parsedNumberValue) ||
      !numberExists(parsedNumberValidationValue) ||
      parsedNumberValue <= parsedNumberValidationValue
    ) {
      message = errorMessages.ERROR_GREATER_THAN_SPECIFIED_VALUE(validationValue as string)
    }
  } else if (type === 'gteValue') {
    if (
      !numberExists(parsedNumberValue) ||
      !numberExists(parsedNumberValidationValue) ||
      parsedNumberValue < parsedNumberValidationValue
    ) {
      message = errorMessages.ERROR_GREATER_THAN_OR_EQUAL_TO_SPECIFIED_VALUE(
        validationValue as string
      )
    }
  } else if (type === 'equalsValue') {
    if (
      !numberExists(parsedNumberValue) ||
      !numberExists(parsedNumberValidationValue) ||
      parsedNumberValue !== parsedNumberValidationValue
    ) {
      message = errorMessages.ERROR_EQUAL_TO_SPECIFIED_VALUE(validationValue as string)
    }
  } else if (type === 'ltField') {
    if (
      !numberExists(parsedNumberValue) ||
      !numberExists(parsedNumberDepValue) ||
      parsedNumberValue >= parsedNumberDepValue
    ) {
      message = errorMessages.ERROR_LESS_THAN_SPECIFIED_FIELD(depValue as string)
    }
  } else if (type === 'lteField') {
    if (
      !numberExists(parsedNumberValue) ||
      !numberExists(parsedNumberDepValue) ||
      parsedNumberValue > parsedNumberDepValue
    ) {
      message = errorMessages.ERROR_LESS_THAN_OR_EQUAL_TO_SPECIFIED_FIELD(depValue as string)
    }
  } else if (type === 'gtField') {
    if (
      !numberExists(parsedNumberValue) ||
      !numberExists(parsedNumberDepValue) ||
      parsedNumberValue <= parsedNumberDepValue
    ) {
      message = errorMessages.ERROR_GREATER_THAN_SPECIFIED_FIELD(depValue as string)
    }
  } else if (type === 'gteField') {
    if (
      !numberExists(parsedNumberValue) ||
      !numberExists(parsedNumberDepValue) ||
      parsedNumberValue < parsedNumberDepValue
    ) {
      message = errorMessages.ERROR_GREATER_THAN_OR_EQUAL_TO_SPECIFIED_FIELD(depValue as string)
    }
  } else if (type === 'equalsField') {
    if (
      !numberExists(parsedNumberValue) ||
      !numberExists(parsedNumberDepValue) ||
      parsedNumberValue !== parsedNumberDepValue
    ) {
      message = errorMessages.ERROR_EQUAL_TO_SPECIFIED_FIELD(depValue as string)
    }
  } else if (fieldType === 'FIELD_DATETIME' || fieldType === 'FIELD_DATE') {
    if (!value) {
      return ''
    }
    // We're looking at a date or date/time field, which can be parsed by a Date() constructor
    let valueTime = null
    let depTime = null
    let validationTime = null
    valueTime = new Date(value as string)
    if (depValue) {
      depTime = new Date(depValue as string)
    }
    if (validationValue) {
      validationTime = new Date(validationValue)
    }
    if (type === 'equalsDate' || type === 'equalsDateTime') {
      if (valueTime !== validationTime) {
        message = errorMessages.ERROR_EQUAL_TO_SPECIFIED_VALUE(validationTime?.toLocaleString())
      }
    } else if (type === 'equalsDateField' || type === 'equalsDateTimeField') {
      if (valueTime !== depTime) {
        message = errorMessages.ERROR_EQUAL_TO_SPECIFIED_FIELD(depTime?.toLocaleString())
      }
    } else if (type === 'afterDateEx' || type === 'afterDateTimeEx') {
      if (valueTime <= validationTime!) {
        message = errorMessages.ERROR_AFTER_SPECIFIED_DATE_EX(validationTime?.toLocaleString())
      }
    } else if (type === 'afterDateIn' || type === 'afterDateTimeIn') {
      if (valueTime < validationTime!) {
        message = errorMessages.ERROR_AFTER_SPECIFIED_DATE_IN(validationTime?.toLocaleString())
      }
    } else if (type === 'beforeDateEx' || type === 'beforeDateTimeEx') {
      if (valueTime >= validationTime!) {
        message = errorMessages.ERROR_BEFORE_SPECIFIED_DATE_EX(validationTime?.toLocaleString())
      }
    } else if (type === 'beforeDateIn' || type === 'beforeDateTimeIn') {
      if (valueTime > validationTime!) {
        message = errorMessages.ERROR_BEFORE_SPECIFIED_DATE_IN(validationTime?.toLocaleString())
      }
    } else if (type === 'afterDateFieldEx' || type === 'afterDateTimeFieldEx') {
      if (valueTime <= depTime!) {
        message = errorMessages.ERROR_AFTER_SPECIFIED_FIELD_EX(depTime?.toLocaleString())
      }
    } else if (type === 'afterDateFieldIn' || type === 'afterDateTimeFieldIn') {
      if (valueTime < depTime!) {
        message = errorMessages.ERROR_AFTER_SPECIFIED_FIELD_IN(depTime?.toLocaleString())
      }
    } else if (type === 'beforeDateFieldEx' || type === 'beforeDateTimeFieldEx') {
      if (valueTime >= depTime!) {
        message = errorMessages.ERROR_BEFORE_SPECIFIED_FIELD_EX(depTime?.toLocaleString())
      }
    } else if (type === 'beforeDateFieldIn' || type === 'beforeDateTimeFieldIn') {
      if (valueTime > depTime!) {
        message = errorMessages.ERROR_BEFORE_SPECIFIED_FIELD_IN(depTime?.toLocaleString())
      }
    }
  } else {
    if (!value) {
      return ''
    }
    // We're working with just times, which can't be parsed correctly by
    // the JS Date() constructor, so parse them accordingly
    let valueTime = null
    let depTime = null
    let validationTime = null
    valueTime = parseTime(value as string, false)
    if (depValue) {
      depTime = parseTime(depValue as string, false)
    }
    if (validationValue) {
      validationTime = parseTime(validationValue, false)
    }

    if (type === 'equalsTime') {
      if (!validationTime || valueTime !== validationTime) {
        message = errorMessages.ERROR_EQUAL_TO_SPECIFIED_VALUE(validationTime!.toLocaleTimeString())
      }
    } else if (type === 'afterTimeIn') {
      if (!validationTime || valueTime < validationTime) {
        message = errorMessages.ERROR_AFTER_SPECIFIED_DATE_IN(validationTime!.toLocaleTimeString())
      }
    } else if (type === 'afterTimeEx') {
      if (!validationTime || valueTime <= validationTime) {
        message = errorMessages.ERROR_AFTER_SPECIFIED_DATE_EX(validationTime!.toLocaleTimeString())
      }
    } else if (type === 'beforeTimeIn') {
      if (!validationTime || valueTime > validationTime) {
        message = errorMessages.ERROR_BEFORE_SPECIFIED_DATE_IN(validationTime!.toLocaleTimeString())
      }
    } else if (type === 'beforeTimeEx') {
      if (!validationTime || valueTime >= validationTime) {
        message = errorMessages.ERROR_BEFORE_SPECIFIED_DATE_EX(validationTime!.toLocaleTimeString())
      }
    } else if (type === 'equalsTimeField') {
      if (!depTime || valueTime !== depTime) {
        message = errorMessages.ERROR_EQUAL_TO_SPECIFIED_VALUE(depTime!.toLocaleTimeString())
      }
    } else if (type === 'afterTimeFieldIn') {
      if (!depTime || valueTime < depTime) {
        message = errorMessages.ERROR_AFTER_SPECIFIED_FIELD_IN(depTime!.toLocaleTimeString())
      }
    } else if (type === 'afterTimeFieldEx') {
      if (!depTime || valueTime <= depTime) {
        message = errorMessages.ERROR_AFTER_SPECIFIED_FIELD_EX(depTime!.toLocaleTimeString())
      }
    } else if (type === 'beforeTimeFieldIn') {
      if (!depTime || valueTime > depTime) {
        message = errorMessages.ERROR_BEFORE_SPECIFIED_FIELD_IN(depTime!.toLocaleTimeString())
      }
    } else if (type === 'beforeTimeFieldEx') {
      if (!depTime || valueTime >= depTime) {
        message = errorMessages.ERROR_BEFORE_SPECIFIED_FIELD_EX(depTime!.toLocaleTimeString())
      }
    }
  }
  return message
}

function numberExists(num: any) {
  return !(num === null || num === undefined || isNaN(num))
}

function parseTime(value: string, isRecapValue: boolean) {
  // The times from the Quasar time picker and from the Recap builder are formatted differently
  if (isRecapValue) {
    // Format: HH:MM AM/PM
    const recapTimePieces: string[] = value.split(/[: ]/)
    const recapNumbers: number[] = []
    recapNumbers[1] = parseInt(recapTimePieces[1])
    if (recapTimePieces[2] === 'PM') {
      recapNumbers[0] = parseInt(recapTimePieces[0]) + 12
    } else {
      recapNumbers[0] = parseInt(recapTimePieces[0])
    }
    const recapTime = new Date(
      // @ts-expect-error We don't care about the year
      null,
      null,
      null,
      recapNumbers[0],
      recapNumbers[1]
    )
    return recapTime
  }
  // Else we're working with a Quasar time picker time
  // Format: HH:MM:mm
  const valueTimePieces: string[] = (value as unknown as string).split(/[: ]/)
  const valueNumbers: number[] = []
  valueNumbers[0] = parseInt(valueTimePieces[0])
  valueNumbers[1] = parseInt(valueTimePieces[1])
  valueNumbers[2] = parseInt(valueTimePieces[2])
  const valueTime = new Date(
    // @ts-expect-error We don't care about the year
    null,
    null,
    null,
    valueNumbers[0],
    valueNumbers[1],
    valueNumbers[2]
  )

  return valueTime
}

// Naive array equality checker
function isArrayEqual(arr1: any[], arr2: any[]) {
  if ((!arr1 && arr2) || (arr1 && !arr2)) return false
  if (arr1.length !== arr2.length) return false
  const arr1Sorted = arr1.sort()
  const arr2Sorted = arr2.sort()
  for (let i = 0; i < arr1.length; i++) {
    if (arr1Sorted[i] !== arr2Sorted[i]) return false
  }
  return true
}

function filterDependencyRefsForRepeatingForm(
  dependencyRefs: Ref<(typeof RecapField)[]>,
  repeatingIndex: number | undefined
) {
  let tempDependenceRefs = dependencyRefs.value
  const dependenceRefFieldIds = tempDependenceRefs.map((ref: typeof RecapField) =>
    (ref as any).getFieldId()
  )
  const isAllDependenceRefsUnique = !dependenceRefFieldIds.some(
    (id, index) => dependenceRefFieldIds.indexOf(id) < index
  )
  if (!isAllDependenceRefsUnique && repeatingIndex !== undefined) {
    // If we get here, we have multiple fields with the same ID AND we're doing logic within a repeating form
    // If we either have a repeating form but are doing logic for a field outside it,
    // or we don't have a repeating form,
    // we will not hit this block, and the logic will work as normal
    let duplicateDependenceRefs = dependencyRefs.value.filter(
      (ref) =>
        dependenceRefFieldIds.indexOf((ref as any).getFieldId()) !==
        dependenceRefFieldIds.lastIndexOf((ref as any).getFieldId())
    )
    const uniqueDependenceRefs = dependencyRefs.value.filter(
      (ref) =>
        dependenceRefFieldIds.indexOf((ref as any).getFieldId()) ===
        dependenceRefFieldIds.lastIndexOf((ref as any).getFieldId())
    )
    duplicateDependenceRefs = duplicateDependenceRefs.filter(
      (ref) => (ref as any).getRepeatingIndex() === repeatingIndex
    )
    tempDependenceRefs = duplicateDependenceRefs.concat(uniqueDependenceRefs)
  }
  return tempDependenceRefs
}
